import {ModelXhr} from '../../helpers/model/xhr.js';
import {applyProjection, addToBoundingBox, createPath} from './functions';
import Transformation from '../../helpers/math/transformation.js';
import TopojsonFeature from '../../helpers/vendor/topojson/feature';
import Vector2 from '../../helpers/math/vector2';
import {cloneObject } from '../../helpers/functions/extend.js';
import { objectsEqual} from '../../helpers/functions/objects_equal.js';

class GeoModel extends ModelXhr{

    static modelName = "GeoModel";

    constructor(selectData){
        
        super(selectData, {
            endpoint: './server/iommap.json',
            method: 'GET'
        });
        this.name = 'GeoModel';

        // this enables caching
        this.cacheEnabled = true;
        this.cacheLimit = 1;

        this.dimension = 'region';
        this.filter = false;
        this.size = [0, 0];
        this.hover = false;

        this.globalBounds = [{x:0, y:0}, {x:0, y:0}];
        this.regionalBBoxes = new Map();
        this.subRegionalBBoxes = new Map();
        this.countryPaths = [];
        this.countryData = new Map();

        this.transformation = null;
        this.currentView = null;
        this.mapHeight = 0;
        this.extraBubbleSize = 0;

        this.getCountryPaths = this.getCountryPaths.bind(this);
        this.getCountryData = this.getCountryData.bind(this);
        this.getLocationCoords = this.getLocationCoords.bind(this);
        this.onHover = this.onHover.bind(this);
        this.setExtraBubbleSize = this.setExtraBubbleSize.bind(this);

        this.heightCallback = function(){};
    }

    getDataFromResponse(data){
        try{
            return JSON.parse(data);
        }catch(e){
            const error = 'Error loading map data: ' + e.message;
            this.onFailure(error);
        }
        return null;
    }

    setViewData(data){
        let filter = cloneObject(data.filter || {});
        if(Object.keys(filter).length === 0){
            filter = false;
        }

        if(this.dimension !== data.dimension || ! objectsEqual(filter, this.filter)){
            this.dimension = data.dimension;
            this.filter = filter;
            this.transformation = null;
            this.currentView = null;
            this.extraBubbleSize = 0;
            this.trigger();
        }
    }

    getCountryPaths(){
        return this.countryPaths;
    }

    getCountryData(){
        return this.countryData;
    }

    getDataCountries(){
        const scale = this.getTransformation().objectScaleFact.x || 1;
        return {
            status: this.status,
            data: {
                getCountryPaths: this.getCountryPaths,
                getCountryData: this.getCountryData,
                transformation: this.getTransformationString(),
                scale: 1/scale,
                hover: this.hover,
                dimension: this.dimension
            }
        };
    }

    getInterface(){
        return {
            status: this.status,
            data: {
                setMaxSize: this.setMaxSize.bind(this),
                setViewData: this.setViewData.bind(this),
                setHeightCallback: this.setHeightCallback.bind(this)
            }
        };
    }

    onHover(dimension, value){
        if(! dimension || ! value){
            this.hover = false;
            
        }else{
            this.hover = {
                dimension: dimension,
                value: value
            };
        }
        this.trigger();
    }

    getDataBubbles(){
        
        return {
            status: this.status,
            data: {
                getLocationCoords: this.getLocationCoords,
                hover: this.hover,
                onHover: this.onHover,
                dimension: this.dimension,
                setExtraBubbleSize: this.setExtraBubbleSize,
                getItemRegion: this.getItemRegion.bind(this)
            }
        };
    }

    setMaxSize(width, height){
        if(this.size[0] === width && this.size[1] === height){
            return;
        }
        this.transformation = null;
        this.currentView = null;
        this.size = [width, height];
        this.trigger();
    }

    setExtraBubbleSize(needsExtraSize){
        const size = needsExtraSize? 50: 0;
        if(this.extraBubbleSize !== size){
            this.extraBubbleSize = size;
            // this.transformation = null;
        }
    }

    getExtraBubbleSize(){
        return this.extraBubbleSize;
    }

    setHeightCallback(callback){
        this.heightCallback = callback;
    }

    setData(data, meta){
        this.transformation = null;
        this.currentView = null;
        this.setCountryData(data);
        this.setCountryPaths(data);
        return super.setData(data, meta);
    }

    setCountryData(data){
        this.globalBounds = [{x:0, y:0}, {x:0, y:0}];
        this.regionalBBoxes = new Map();
        this.subRegionalBBoxes = new Map();

        this.countryData = new Map();

        for(let id of Object.keys(data.details)){
            const d = data.details[id];
            d.size = 0;
            if(d.bbox && d.bbox.length === 4){

                const b1 = applyProjection([d.bbox[2], d.bbox[1]]);
                const b2 = applyProjection([d.bbox[0], d.bbox[3]]);

                // if(d.id3 === 'RUS'){
                //     b2[0] = applyProjection([37.545556, 55.7422318])[0];
                // }
                
                d.localBBox = [{x: b1[0], y:b1[1]}, {x: b2[0], y: b2[1]}];
                d.size = Math.abs((b2[0]+200) - (b1[0]+200)) * Math.abs((b2[1]+100) - (b1[0] + 100));
            }else{
                d.localBBox = [{x: 0, y:0}, {x: 0, y: 0}];
            }
            if(d.centroid && d.centroid.length === 2){
                d.localCentroid = applyProjection([d.centroid[1], d.centroid[0]]);
            }else{
                d.localCentroid = [0.5*(d.localBBox[1].x + d.localBBox[0].x), 
                        0.5*(d.localBBox[1].y - d.localBBox[0].y)]; 
            }

            // if(d.id3 === 'RUS'){
            //     d.localCentroid = applyProjection([37.545556, 55.7422318]);
            // }

            this.countryData.set(d.id3, d);

            if(d.region && d.region !== ''){
                let rbox = [];
                if(this.regionalBBoxes.has(d.region)){
                    rbox = this.regionalBBoxes.get(d.region); 
                }else{
                    rbox = [{x:0, y:0}, {x:0, y:0}];
                }
                addToBoundingBox(rbox, d.localBBox);
                this.regionalBBoxes.set(d.region, rbox);
            }
            if(d.subregion && d.subregion !== ''){
                let rbox = [];
                if(this.subRegionalBBoxes.has(d.subregion)){
                    rbox = this.subRegionalBBoxes.get(d.subregion); 
                }else{
                    rbox = [{x:0, y:0}, {x:0, y:0}];
                }
                addToBoundingBox(rbox, d.localBBox);
                this.subRegionalBBoxes.set(d.subregion, rbox);
            }

            addToBoundingBox(this.globalBounds, d.localBBox);

        }


    }

    getItemRegion(dimension, value){
        // if(value === 'Côte d\'Ivoire'){
        //     value = 'Cote d\'Ivoire';
        // }else if(value === 'Venezuela, Bolivarian Republic of'){
        //     value = 'Venezuela';
        // }
        for(const entry of this.countryData){
            const country = entry[1];

            if(country[dimension] === value){
                return country.region;
            }
        }
        return 'default';
    }

    setCountryPaths(data){
        const countries = TopojsonFeature(data.topojson, 
                data.topojson.objects.zolaboMap);

        const countryIndexes = {};

        for(let i = 0; i < countries.features.length; i++){
            const c = countries.features[i];
            
            // country without geometry. This happens for extreme small shapes. They are not visible on this
            // level of detail.
            if(c.geometry === null){
                continue;
            }

            if(this.countryData.has(c.id)){
                let data = this.countryData.get(c.id);
                c.size = data.size;
            }else{
                c.size = 0;
            }

            c.path = createPath(c.geometry.coordinates);

            if(countryIndexes[c.id]){
                // Multiple paths for the same country found. Merge them
                this.countryPaths[countryIndexes[c.id]].path += ' ' + c.path;

            }else{  
                countryIndexes[c.id] = this.countryPaths.length;
                this.countryPaths.push(c);
            }
        }

        this.countryPaths.sort(function(a, b){
            return (b.size - a.size);
        });

    }

    isInSelection(d, selected){

        if(! selected){
            return true;
        }
        if(! (selected instanceof Array)){
            selected = [ selected ];
        }

        if(typeof d === 'string'){
            d = this.countryData.get(d);
        }
        if(! d){
            return false;
        }

        for(const sel of selected){
            let type = sel.type;
            if(type === 'country_iso2'){
                type = 'id2';
            }else if(type === 'country_id'){
                type = 'id2';
            }else if(type === 'country_iso3'){
                type = 'id3';
            }

            if((sel.value instanceof Array) && sel.value.indexOf(d[type]) !== -1){
                return true;
            }else if(sel.value === d[type]){
                return true;
            }
        }

        return false;
        
    }

    getBounds(values){
        const globalBounds = [ {x: 0, y: 0}, {x: 0, y: 0} ];

        this.countryData.forEach((d) => {
            if(this.isInSelection(d, values)){
                if(d.localBBox){
                    addToBoundingBox(globalBounds, d.localBBox);
                }
            }
        });

        return globalBounds;
    }

    getView(value=null, dimension='region', getCentroid=false, filter=false){

        let bbox = [new Vector2(), new Vector2()];
        const ids = [];

        // find the matching country data (coordinates)    
        for(const entry of this.countryData){
            const country = entry[1];
            
            if(filter){
                let include = true;
                for(const dim of Object.keys(filter)){
                    if(filter[dim] && ! country[dim] && dim !== 'country'){
                        include = false;
                    }else if(filter[dim] instanceof Array && filter[dim].indexOf(country[dim]) === -1){
                        include = false;
                    }else if((filter[dim] && country[dim] && filter[dim] !== country[dim]) || 
                            (filter.country && country.id2 && country.id2 !== filter.country)){
                        include = false;
                    }
                }
                if(! include){
                    continue;
                }
            }

            if(value === null ||
                    (dimension === 'region' && country.region === value) ||
                    (dimension === 'subregion' && country.subregion === value) ||
                    (dimension === 'country' &&  country.country === value) || 
                    (dimension === 'country' &&  country.id2 === value) || 
                    (dimension === 'iso' &&  country.id3 === value)){

                if(! (country.localBBox[0].x === 0 && country.localBBox[1].x === 0 && 
                        country.localBBox[0].y === 0 && country.localBBox[1].y === 0)){
                    bbox = addToBoundingBox(bbox, country.localBBox);
                }

                if(getCentroid && (dimension === 'country' || dimension === 'iso3')){

                    const centroid = new Vector2(
                        country.localCentroid[0] || 0,
                        country.localCentroid[1] || 0
                    );
                    return centroid;
                }
                
                if(ids.indexOf(country.id3) === -1){
                    ids.push(country.id3);
                }

            }
        }

        if(ids.length === 0){
            return null;
        }

        // calculate the centroid of a region
        const centroid = new Vector2(
                0.5*(bbox[1].x + bbox[0].x),
                0.5*(bbox[1].y + bbox[0].y));

        if(getCentroid){
            return centroid;
        }

        return {
            ids: ids,
            bbox: bbox,
            centroid: centroid,
        };
    }

    getTransformation(){
        if(this.transformation !== null){
            return this.transformation;
        }

        let view = this.getView(null, this.dimension, false, this.filter);

        if(view === null){
            view = {
                bbox: this.globalBounds,
                centroid: new Vector2(
                    0.5*(this.globalBounds[1].x + this.globalBounds[0].x),
                    0.5*(this.globalBounds[1].y + this.globalBounds[0].y))
            }
            // return this.transformation;
        }

        this.currentView = view;
        this.transformation = new Transformation();

        if(view.bbox[0].x === 0 && view.bbox[1].x === 0){
            return this.transformation;
        }

        const scaleX = this.size[0] / (view.bbox[1].x - view.bbox[0].x);
        const scaleY = this.size[1] / (view.bbox[1].y - view.bbox[0].y);
        const scale = Math.min(scaleX, scaleY);

        this.transformation.setPosition(-scale*view.centroid.x, -scale*view.centroid.y, 0);
        this.transformation.setObjectScale({
            x: scale, 
            y: scale
        });
        this.heightCallback(this.getMapHeight(this.size[0] * scale / scaleX));

        this.hover = false;

        return this.transformation;
    }

    getMapHeight(width){
        if(this.currentView === null){
            this.currentView = this.getView(null, this.dimension, false, this.filter);
        }
        const box = this.currentView.bbox;
        return Math.round(Math.abs((box[1].y - box[0].y) * width / (box[1].x - box[0].x)));
    }

    getTransformationString(){
        const transformation = this.getTransformation();
        return transformation.getTransformationString();
    }

    getTransformationMatrix(){
        const transformation = this.getTransformation();
        return transformation.getTransformationMatrix();
    }

    getLocationCoords(value, dimension){
        const centroid = this.getView(value, dimension, true);

        if(centroid === null){
            return null;
        }
        const matrix = this.getTransformationMatrix();
        let result = matrix.multiplyVector([centroid.x, centroid.y]);
        return result;
    }


}


export {GeoModel};